import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/api";

/**
 * Iter74 — Contexte global d'annee fiscale.
 *
 * Priorite de selection :
 *   1. Selection manuelle (user dropdown — local ou global)
 *   2. default_tax_year stocke dans settings backend
 *   3. null (= toutes annees, comportement historique)
 *
 * - L'override manuel persiste pour la session courante uniquement.
 * - Au reload, le default est ré-appliqué.
 * - Si default_tax_year n'est pas defini → fallback null (toutes).
 */
const FiscalYearContext = createContext(null);

export function FiscalYearProvider({ children }) {
  const [defaultYear, setDefaultYear] = useState(null);
  const [year, setYearState] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  // Hydrate from backend settings on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const r = await api.get("/settings");
        const dy = r.data?.default_tax_year ?? null;
        if (!mounted) return;
        setDefaultYear(dy);
        setYearState(dy);
      } catch {
        if (mounted) setDefaultYear(null);
      } finally {
        if (mounted) setHydrated(true);
      }
    })();
    // Listen for settings updates (Settings page emits this)
    const handler = (e) => {
      const fields = e?.detail || [];
      if (fields.includes("default_tax_year")) {
        api.get("/settings").then(r => {
          const dy = r.data?.default_tax_year ?? null;
          setDefaultYear(dy);
          setYearState(dy);
        }).catch(() => {});
      }
    };
    window.addEventListener("settings:updated", handler);
    return () => {
      mounted = false;
      window.removeEventListener("settings:updated", handler);
    };
  }, []);

  const setYear = useCallback((next) => {
    setYearState(next === "" || next === undefined ? null : next);
  }, []);

  const resetToDefault = useCallback(() => setYearState(defaultYear), [defaultYear]);

  return (
    <FiscalYearContext.Provider value={{ year, setYear, defaultYear, resetToDefault, hydrated }}>
      {children}
    </FiscalYearContext.Provider>
  );
}

export function useFiscalYear() {
  const ctx = useContext(FiscalYearContext);
  if (!ctx) {
    // Safe fallback when used outside provider (e.g. portal pages)
    return { year: null, setYear: () => {}, defaultYear: null, resetToDefault: () => {}, hydrated: true };
  }
  return ctx;
}

export default FiscalYearContext;
